import React, { useState } from 'react';
import { useModalDismiss } from '../hooks/useModalDismiss';
import { type Book } from '../types';
import { Undo2, Trash2, X, AlertTriangle } from 'lucide-react';

interface TrashPanelProps {
  isOpen: boolean;
  deletedBooks: Book[];
  onClose: () => void;
  onRestore: (ids: string[]) => void;
  onPermanentDelete: (ids: string[]) => void;
}

interface PermanentDeleteModalProps {
  isOpen: boolean;
  count: number;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading: boolean;
}

function PermanentDeleteModal({ isOpen, count, onClose, onConfirm, loading }: PermanentDeleteModalProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const dismiss = useModalDismiss(onClose);

  const handleConfirm = async () => {
    setError('');
    await onConfirm();
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .trash-password-input {
          -webkit-text-security: disc;
          -moz-text-security: disc;
          text-security: disc;
        }
      `}</style>
      <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'var(--modal-mask)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002,
      backdropFilter: 'blur(4px)'
    }} {...dismiss}>
      <div style={{
        backgroundColor: 'var(--card-bg)', padding: 32, borderRadius: 14, width: 380,
        boxShadow: 'var(--shadow-lg)', color: 'var(--text-color)', textAlign: 'center'
      }}>
        <div style={{ marginBottom: 12, color: 'var(--danger-color)' }}>
          <AlertTriangle size={36} />
        </div>
        <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 700 }}>彻底删除作品？</h3>
        <p style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
          这将永久删除 <span style={{ color: 'var(--danger-color)', fontWeight: 700 }}>{count}</span> 部作品及其所有内容。<br/>此操作无法撤销。
        </p>
        <input
          type="text"
          value={password}
          onChange={e => { setPassword(e.target.value); setError(''); }}
          placeholder="输入密码以确认删除"
          autoComplete="off"
          name="trash-delete-confirm"
          data-lpignore="true"
          readOnly={false}
          className="trash-password-input"
          style={{ width: '100%', padding: '12px 14px', marginBottom: 8, boxSizing: 'border-box', borderRadius: 8, border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-color)', color: 'var(--text-color)', fontSize: 15, textAlign: 'center', outline: 'none', fontFamily: 'inherit' }}
        />
        {error && <p style={{ color: 'var(--danger-color)', fontSize: 13, margin: '4px 0' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose} disabled={loading} style={{ flex: 1, padding: '12px', border: '1px solid var(--border-color)', backgroundColor: 'transparent', color: 'var(--text-secondary)', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600 }}>取消</button>
          <button 
            onClick={handleConfirm}
            disabled={loading || password.length === 0}
            style={{ 
              flex: 1, padding: '12px', backgroundColor: 'var(--danger-color)', color: '#fff', border: 'none', borderRadius: 8, 
              cursor: (loading || password.length === 0) ? 'not-allowed' : 'pointer', fontWeight: 600,
              opacity: (loading || password.length === 0) ? 0.5 : 1
            }}
          >
            {loading ? '处理中...' : '彻底删除'}
          </button>
        </div>
        </div>
      </div>
    </>
  );
}

// ============ 回收站面板主组件 ============
export function TrashPanel({ isOpen, deletedBooks, onClose, onRestore, onPermanentDelete }: TrashPanelProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmptyModal, setShowEmptyModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [targetBookId, setTargetBookId] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setTargetBookId(null);
      onClose();
    }, 300);
  };

  // 单本恢复
  const handleRestore = async (id: string) => {
    await onRestore([id]);
  };

  // 单本删除
  const handleDeleteClick = (id: string) => {
    setTargetBookId(id);
    setShowDeleteModal(true);
  };

  // 删除确认
  const handleDeleteConfirm = async () => {
    if (targetBookId === null) return;
    setDeleteLoading(true);
    await window.api.permanentlyDeleteBooks([targetBookId]);
    setDeleteLoading(false);
    setShowDeleteModal(false);
    setTargetBookId(null);
    onPermanentDelete([targetBookId]);
  };

  // 清空回收站
  const handleEmptyTrash = () => {
    if (deletedBooks.length > 0) {
      setShowEmptyModal(true);
    }
  };

  const handleEmptyConfirm = async () => {
    setDeleteLoading(true);
    const allIds = deletedBooks.map(w => w.id);
    await window.api.permanentlyDeleteBooks(allIds);
    setDeleteLoading(false);
    setShowEmptyModal(false);
    setTargetBookId(null);
    onPermanentDelete(allIds);
  };

  if (!isOpen) return null;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <div
        onClick={handleClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'var(--modal-mask)', zIndex: 999,
          backdropFilter: 'blur(4px)',
          animation: isClosing ? 'fadeOut 0.25s ease-out forwards' : 'fadeIn 0.25s ease-out'
        }}
      />
      
      {/* 面板容器：flexbox 居中，动画作用在内层 div */}
      <div style={{
        position: 'fixed', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, pointerEvents: 'none'
      }}>
        <div style={{
          width: '82vw', maxWidth: 900, height: '72vh', maxHeight: 700,
          backgroundColor: 'var(--card-bg)', borderRadius: 14,
          boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column',
          pointerEvents: 'auto',
          animation: isClosing ? 'fadeOut 0.2s ease-out forwards' : 'scaleUp 0.2s cubic-bezier(0.34, 1.2, 0.64, 1)',
          overflow: 'hidden'
        }}>
        {/* 头部 */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 24px', borderBottom: '1px solid var(--border-color)',
          flexShrink: 0, minHeight: 52
        }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Trash2 size={17} />
            回收站
            <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 4 }}>
              ({deletedBooks.length})
            </span>
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={handleEmptyTrash}
              disabled={deletedBooks.length === 0}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 14px', border: '1px solid var(--border-color)',
                borderRadius: 6, background: 'transparent', color: deletedBooks.length > 0 ? 'var(--danger-color)' : 'var(--text-secondary)',
                fontSize: 12, fontWeight: 500, cursor: deletedBooks.length > 0 ? 'pointer' : 'not-allowed',
                opacity: deletedBooks.length === 0 ? 0.4 : 1, transition: 'all 0.15s'
              }}
            >
              <Trash2 size={13} />
              清空回收站
            </button>
            <button
              onClick={handleClose}
              style={{
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', background: 'transparent', color: 'var(--text-secondary)',
                borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--hover-bg)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* 表头 */}
        {deletedBooks.length > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1.5fr 100px 100px 140px',
            padding: '10px 24px', borderBottom: '1px solid var(--border-color)',
            fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
            textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0
          }}>
            <span>作品名称</span>
            <span>简介</span>
            <span>创建时间</span>
            <span>删除时间</span>
            <span style={{ textAlign: 'right', paddingRight: 8 }}>操作</span>
          </div>
        )}

        {/* 列表 */}
        <div style={{
          flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0
        }}>
          {deletedBooks.length === 0 ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', color: 'var(--text-secondary)', gap: 10
            }}>
              <span style={{ fontSize: 36, opacity: 0.3 }}>📭</span>
              <span style={{ fontSize: 14, opacity: 0.5 }}>回收站是空的</span>
            </div>
          ) : (
            deletedBooks.map((book) => (
              <div
                key={book.id}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 1.5fr 100px 100px 140px',
                  padding: '12px 24px', borderBottom: '1px solid var(--border-color)',
                  alignItems: 'center', fontSize: 13, color: 'var(--text-color)',
                  transition: 'background-color 0.1s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--hover-bg)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {/* 名称 */}
                <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={book.title}>
                  {book.title}
                </span>
                {/* 简介 */}
                <span style={{
                  color: 'var(--text-secondary)', fontSize: 12,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }} title={book.description || ''}>
                  {book.description || '—'}
                </span>
                {/* 创建时间 */}
                <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                  {formatDate(book.created_at)}
                </span>
                {/* 删除时间 */}
                <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                  {formatDate(book.updated_at)}
                </span>
                {/* 操作按钮 */}
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => handleRestore(book.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '5px 10px', border: '1px solid var(--border-color)',
                      borderRadius: 5, background: 'transparent', color: 'var(--primary-color)',
                      fontSize: 11, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--primary-color-light)'; e.currentTarget.style.borderColor = 'var(--primary-color)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'var(--border-color)'; }}
                  >
                    <Undo2 size={12} /> 恢复
                  </button>
                  <button
                    onClick={() => handleDeleteClick(book.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '5px 10px', border: '1px solid var(--border-color)',
                      borderRadius: 5, background: 'transparent', color: 'var(--danger-color)',
                      fontSize: 11, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(200, 75, 75, 0.06)'; e.currentTarget.style.borderColor = 'var(--danger-color)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'var(--border-color)'; }}
                  >
                    <Trash2 size={12} /> 删除
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      </div>

      {/* 删除确认弹窗 */}
      <PermanentDeleteModal
        isOpen={showDeleteModal}
        count={targetBookId ? 1 : 0}
        onClose={() => { setShowDeleteModal(false); setTargetBookId(null); }}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />

      {/* 清空确认弹窗 */}
      <PermanentDeleteModal
        isOpen={showEmptyModal}
        count={deletedBooks.length}
        onClose={() => setShowEmptyModal(false)}
        onConfirm={handleEmptyConfirm}
        loading={deleteLoading}
      />
    </>
  );
}
